import { AlertTriangle, CheckCircle2, Circle, Download } from 'lucide-react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import type {
  PublicServiceRequest,
  ServiceRequestStatus,
} from '@repo/contracts';
import { AUDIT_STANDARD_LABELS } from '@repo/contracts/services';
import {
  formatMoney,
  REPORT_TURNAROUND_LABEL,
} from '@repo/contracts/services-pricing';

import { trackEvent } from '../analytics/posthog';
import { ApiError } from '../api/client';
import { useServiceRequest } from '../api/services';
import { CopyButton } from '../components/copy-button';
import { cx } from '../components/cx';
import {
  ButtonLink,
  Card,
  Container,
  Eyebrow,
  formatDate,
  Tag,
} from '../components/ui';
import { CONTACT_EMAIL } from '../content/site';

const STEPS: { key: ServiceRequestStatus; label: string; detail: string }[] = [
  {
    detail: 'We have your request.',
    key: 'submitted',
    label: 'Request received',
  },
  {
    detail: 'We are confirming pages and access with you.',
    key: 'scoping',
    label: 'Scoping',
  },
  {
    detail: 'Teaser report and invoice sent.',
    key: 'quoted',
    label: 'Quote sent',
  },
  {
    detail: `Full report usually within ${REPORT_TURNAROUND_LABEL}, depending on scope.`,
    key: 'paid',
    label: 'Paid',
  },
  {
    detail: 'Report and badge delivered.',
    key: 'delivered',
    label: 'Delivered',
  },
];

const ORDER: ServiceRequestStatus[] = [
  'submitted',
  'scoping',
  'quoted',
  'paid',
  'delivered',
];

function Timeline({ status }: { status: ServiceRequestStatus }) {
  const current = ORDER.indexOf(status);
  return (
    <ol className="space-y-0">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li className="relative flex gap-4 pb-6 last:pb-0" key={step.key}>
            {i < STEPS.length - 1 ? (
              <span
                aria-hidden
                className={cx(
                  'absolute left-2.5 top-6 h-full w-px',
                  done ? 'bg-ink' : 'bg-hairline-alt',
                )}
              />
            ) : null}
            <span className="relative z-10 mt-0.5 bg-surface">
              {done ? (
                <CheckCircle2 aria-hidden className="text-ink" size={20} />
              ) : active ? (
                <span
                  aria-hidden
                  className="flex size-5 items-center justify-center rounded-pill bg-brand-strong"
                >
                  <span className="size-2 rounded-pill bg-white" />
                </span>
              ) : (
                <Circle aria-hidden className="text-text-secondary" size={20} />
              )}
            </span>
            <div>
              <p
                aria-current={active ? 'step' : undefined}
                className={cx(
                  'text-ui-lg',
                  active || done
                    ? 'font-semibold text-text-primary'
                    : 'text-text-secondary',
                )}
              >
                {step.label}
                <span className="sr-only">
                  {done
                    ? ' (complete)'
                    : active
                      ? ' (current)'
                      : ' (not started)'}
                </span>
              </p>
              {active ? (
                <p className="mt-0.5 text-ui text-text-secondary">
                  {step.detail}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Summary({ request }: { request: PublicServiceRequest }) {
  const money = request.quote ?? request.estimate;
  return (
    <Card>
      <h2 className="text-small-label uppercase text-text-secondary">
        {request.quote ? 'Your quote' : 'Your estimate'}
      </h2>
      <p className="mt-2 font-report text-report-stat font-medium tabular-nums text-text-primary">
        {formatMoney(money.total, money.currency)}
      </p>
      <p className="mt-1 text-ui text-text-secondary">
        {money.pages} {money.pages === 1 ? 'page' : 'pages'} ×{' '}
        {formatMoney(money.ratePerPage, money.currency)}
      </p>
      <dl className="mt-6 space-y-3 border-t border-hairline pt-6 text-ui">
        <div>
          <dt className="text-text-secondary">Site</dt>
          <dd className="break-all font-medium text-text-primary">
            {request.url}
          </dd>
        </div>
        <div>
          <dt className="text-text-secondary">Standard</dt>
          <dd className="font-medium text-text-primary">
            {AUDIT_STANDARD_LABELS[request.standard]}
          </dd>
        </div>
        <div>
          <dt className="text-text-secondary">Requested</dt>
          <dd className="font-medium text-text-primary">
            {formatDate(request.createdAt)}
          </dd>
        </div>
        {request.documents.length > 0 ? (
          <div>
            <dt className="text-text-secondary">Documents</dt>
            <dd className="font-medium text-text-primary">
              <ul>
                {request.documents.map((d) => (
                  <li className="truncate" key={d.name}>
                    {d.name}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ) : null}
      </dl>
    </Card>
  );
}

function TeaserCard({ request }: { request: PublicServiceRequest }) {
  const { quote, teaser } = request;
  if (!teaser && !quote) return null;
  const counts = teaser
    ? ([
        ['Critical', teaser.summary.critical, 'bg-tag-critical'],
        ['Serious', teaser.summary.serious, 'bg-tag-major'],
        ['Moderate', teaser.summary.moderate, 'bg-tag-minor'],
        ['Minor', teaser.summary.minor, 'bg-warm'],
      ] as const)
    : [];
  return (
    <Card>
      <h2 className="text-card-title text-text-primary">What we found</h2>
      {teaser ? (
        <>
          <p className="mt-3 text-body text-text-primary">{teaser.headline}</p>
          <dl className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {counts.map(([label, n, fill]) => (
              <div
                className={`flex flex-col-reverse rounded-thumb p-4 ${fill}`}
                key={label}
              >
                <dt className="text-caption text-text-secondary">{label}</dt>
                <dd className="font-report text-display-title font-medium text-text-primary">
                  {n}
                </dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        {request.status === 'quoted' && quote?.invoiceUrl ? (
          <ButtonLink
            onClick={() => trackEvent('invoice_opened', { total: quote.total })}
            to={quote.invoiceUrl}
          >
            Pay {formatMoney(quote.total, quote.currency)} and get the full
            report
          </ButtonLink>
        ) : null}
        {teaser ? (
          <ButtonLink
            onClick={() => trackEvent('teaser_downloaded')}
            to={teaser.downloadUrl}
            variant="secondary"
          >
            <Download aria-hidden size={18} /> Download teaser (PDF)
          </ButtonLink>
        ) : null}
      </div>
      {request.status === 'quoted' ? (
        <p className="mt-3 text-ui text-text-secondary">
          Your full report usually arrives within {REPORT_TURNAROUND_LABEL} of
          payment, depending on scope. Need a purchase order or bank transfer?
          Reply to our email.
        </p>
      ) : null}
    </Card>
  );
}

function ReportCard({ request }: { request: PublicServiceRequest }) {
  if (!request.report) return null;
  const { badge } = request;
  return (
    <Card>
      <h2 className="text-card-title text-text-primary">
        Your report is ready
      </h2>
      {request.deliveredAt ? (
        <p className="mt-1 text-ui text-text-secondary">
          Delivered {formatDate(request.deliveredAt)}
        </p>
      ) : null}
      <ButtonLink
        className="mt-5"
        onClick={() => trackEvent('report_downloaded')}
        to={request.report.downloadUrl}
      >
        <Download aria-hidden size={18} /> Download full report
      </ButtonLink>
      {badge ? (
        <div className="mt-8 border-t border-hairline pt-6">
          <h3 className="text-subtitle font-semibold text-text-primary">
            Your Floto badge
          </h3>
          <p className="mt-1 text-ui text-text-secondary">
            Valid for {badge.domain} until {formatDate(badge.expiresAt)}. Paste
            this HTML into your site&rsquo;s footer or accessibility statement.
          </p>
          <div className="mt-4 rounded-thumb border border-hairline bg-warm p-5">
            <img
              alt={`Accessibility audited by Floto, ${AUDIT_STANDARD_LABELS[badge.standard]}`}
              src={badge.imageUrl}
            />
          </div>
          <label
            className="mt-4 block text-ui-strong font-semibold text-text-primary"
            htmlFor="embed-code"
          >
            Embed code
          </label>
          <textarea
            className="mt-2 block min-h-24 w-full rounded-button border border-text-muted bg-surface p-3 font-mono text-ui text-text-primary"
            id="embed-code"
            readOnly
            value={badge.embedHtml}
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <CopyButton
              label="Copy embed code"
              onCopy={() => trackEvent('badge_embed_copied')}
              text={badge.embedHtml}
            />
            <ButtonLink
              size="sm"
              to={`/verify/${badge.id}`}
              variant="secondary"
            >
              View verification page
            </ButtonLink>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export function StatusPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const failedUploads =
    (location.state as { failedUploads?: string[] } | null)?.failedUploads ??
    [];
  const isNew = params.get('new') === '1';
  const query = useServiceRequest(token);

  if (query.isPending) {
    return (
      <Container className="py-28">
        <p aria-live="polite" className="text-body text-text-secondary">
          Loading your request…
        </p>
      </Container>
    );
  }

  if (query.isError) {
    const notFound =
      query.error instanceof ApiError && query.error.status === 404;
    return (
      <Container className="py-28">
        <h1 className="font-report text-report-section font-medium text-text-primary">
          {notFound
            ? 'We couldn’t find this request'
            : 'We couldn’t load your request'}
        </h1>
        <p className="mt-3 max-w-xl text-report-lede text-text-secondary">
          {notFound
            ? 'Check that you copied the whole link from your email. '
            : 'Please refresh the page in a moment. '}
          If you need help, email{' '}
          <a
            className="font-medium text-text-primary underline"
            href={`mailto:${CONTACT_EMAIL}`}
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Container>
    );
  }

  const request = query.data;
  const cancelled = request.status === 'cancelled';

  return (
    <Container className="py-14 md:py-20">
      {isNew ? (
        <div
          className="mb-10 rounded-card border border-hairline bg-tag-success p-6"
          role="status"
        >
          <p className="text-subtitle font-semibold text-text-primary">
            Thanks, {request.contactName} — your request is in.
          </p>
          <p className="mt-1 text-ui-lg text-text-primary">
            We&rsquo;ve emailed this page&rsquo;s link to you. We&rsquo;ll be in
            touch within one business day to confirm scope. Bookmark this page
            to check progress.
          </p>
        </div>
      ) : null}
      {failedUploads.length > 0 ? (
        <div
          className="mb-10 flex gap-3 rounded-card border border-hairline bg-tag-major p-6"
          role="alert"
        >
          <AlertTriangle
            aria-hidden
            className="mt-0.5 shrink-0 text-text-primary"
            size={20}
          />
          <p className="text-ui-lg text-text-primary">
            {failedUploads.length === 1
              ? 'One file'
              : `${failedUploads.length} files`}{' '}
            didn&rsquo;t upload ({failedUploads.join(', ')}). Your request is
            saved — please email {failedUploads.length === 1 ? 'it' : 'them'} to{' '}
            <a
              className="font-medium underline"
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Documents for ${request.reference}`)}`}
            >
              {CONTACT_EMAIL}
            </a>{' '}
            quoting {request.reference}.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Audit request {request.reference}</Eyebrow>
          <h1 className="mt-3 font-report text-report-title font-medium text-text-primary">
            {request.company}
          </h1>
        </div>
        {cancelled ? <Tag tone="critical">Cancelled</Tag> : null}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          {cancelled ? (
            <Card>
              <h2 className="text-card-title text-text-primary">
                This request was cancelled
              </h2>
              <p className="mt-2 text-body text-text-secondary">
                If that&rsquo;s unexpected, or you&rsquo;d like to start again,
                email{' '}
                <a
                  className="font-medium text-text-primary underline"
                  href={`mailto:${CONTACT_EMAIL}`}
                >
                  {CONTACT_EMAIL}
                </a>{' '}
                quoting {request.reference}.
              </p>
            </Card>
          ) : (
            <Card>
              <h2 className="sr-only">Progress</h2>
              <Timeline status={request.status} />
            </Card>
          )}
          <ReportCard request={request} />
          <TeaserCard request={request} />
        </div>
        <div className="lg:col-span-4">
          <Summary request={request} />
        </div>
      </div>
    </Container>
  );
}
